/**
 * Phase 126.L — Linked support plans strip for the unit workspace.
 *
 * Renders the Hub support plans (ADHD / Gifted / Autism) the teacher
 * has attached to THIS unit activity. Each pill links to the saved
 * plan's "Open" view (the generator page rehydrated from the saved
 * plan body).
 *
 * Stays silent (returns null) when:
 *   - the activity isn't a unit (only units can be linked-to)
 *   - the teacher has no plans attached to this unit
 *   - the fetch hasn't resolved yet (no skeleton — avoid CLS on the
 *     top of the workspace)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, Lightbulb, Puzzle, Sparkles } from "lucide-react";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PROFILE_ICON = { adhd: Brain, gifted: Sparkles, autism: Puzzle };
const PROFILE_URL = { adhd: "adhd", gifted: "giftedness", autism: "autism" };
const PROFILE_ACCENT = {
  adhd: "bg-[#96ded1]/25 border-[#96ded1]/60 hover:bg-[#96ded1]/40",
  gifted: "bg-[#f6c177]/25 border-[#f6c177]/60 hover:bg-[#f6c177]/40",
  autism: "bg-[#7aa2f7]/25 border-[#7aa2f7]/60 hover:bg-[#7aa2f7]/40",
};

export default function UnitLinkedSupportPlans({ unitId, isUnit }) {
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    if (!unitId || !isUnit) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${apiBase()}/classroom-support/plans?unit_id=${encodeURIComponent(unitId)}`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPlans(data.plans || []);
      } catch {
        /* silent — strip just doesn't appear */
      }
    })();
    return () => { cancelled = true; };
  }, [unitId, isUnit]);

  if (!isUnit) return null;
  if (!plans || plans.length === 0) return null;

  return (
    <section
      className="mt-4 rounded-xl border border-[#4a6984]/15 bg-white/60 backdrop-blur-sm p-3.5 sm:p-4"
      data-testid="unit-linked-support-plans"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[#1c2d3d] shrink-0">
          <Lightbulb className="h-4 w-4 text-[#f6c177]" strokeWidth={2.2} />
          <p className="text-[11px] uppercase tracking-[0.2em] font-extrabold">
            Support plans for this unit
          </p>
        </div>
        <ul className="flex items-center gap-2 flex-wrap">
          {plans.map((p) => {
            const Icon = PROFILE_ICON[p.profile] || Lightbulb;
            const slug = PROFILE_URL[p.profile] || p.profile;
            const accent =
              PROFILE_ACCENT[p.profile] || "bg-[#96ded1]/20 border-[#96ded1]/50";
            return (
              <li key={p.id}>
                <Link
                  to={`/classroom-support/${slug}/generator?plan=${encodeURIComponent(p.id)}`}
                  data-testid={`unit-linked-support-plan-${p.id}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${accent} text-[11px] font-extrabold text-[#1c2d3d] transition`}
                >
                  <Icon className="h-3 w-3" strokeWidth={2.4} />
                  {p.label}
                  {p.grade ? <span className="font-bold opacity-70">· Gr {p.grade}</span> : null}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
